/**
 * The "other way in" link at the foot of an auth card — "New to WagePro?
 * Create a company account" on `/login`, "Back to sign in" on `/signup`.
 *
 * One component for both, because they had drifted into two identical
 * hand-rolled copies: a bare `<a className="underline">` inside a muted `<p>`,
 * plus the same three-line `replaceState` + `reload()` click handler pasted
 * twice. That is the duplicate-implementation shape
 * `docs/design-system/CLAUDE.md` warns about — the next edit to one would have
 * silently diverged from the other.
 *
 * Visual intent: a SECONDARY path, clearly subordinate to the primary submit
 * button but unmistakably actionable. There is NO text-decoration in any state
 * — not at rest, not on hover: a full-width underline across the whole sentence
 * read as unstyled default-browser markup. The affordance is the accent colour
 * plus medium weight, deepening to `--accent-soft` on hover; keyboard users get
 * a real `focus-visible` ring, so discoverability never depended on the
 * underline.
 *
 * Deliberately no `Separator` here. One was tried and removed: `Separator`
 * styles itself with `bg-divider`, which resolves from `--color-divider` in
 * `packages/ledger-tokens/src/web-tokens.css` — a stylesheet `wagepro-web`'s
 * `index.css` does NOT import (it takes `tokens.css` + `web-glass.css` only).
 * The rule therefore produced no colour and the element rendered as an
 * invisible 1px row. Shipping a no-op element is worse than shipping none, and
 * the card's own padding already separates this from the submit button.
 *
 * Colours come from `--accent`/`--ink-muted` (CSS-variable form, matching the
 * surrounding screens) — never a hardcoded hex — so this inherits WagePro's
 * green from `wagepro-theme.css` and would re-theme with the product.
 */
export function AuthAltActionLink({
  href,
  label,
}: {
  /** Target path — also the real `href`, so middle-click/copy-link behave. */
  href: string;
  /** Already-translated link text; this component never holds copy itself. */
  label: string;
}): React.JSX.Element {
  return (
    <div className="mt-5">
      <p className="text-center text-sm text-[color:var(--ink-muted)]">
        <a
          href={href}
          className="rounded-sm font-medium text-[color:var(--accent)] no-underline transition-colors hover:text-[color:var(--accent-soft)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2"
          onClick={(event) => {
            // These screens are reached by path (not the hash router), and the
            // app boots its auth state once at mount — so the switch is a real
            // navigation, not a client-side route change. `replaceState` first
            // keeps the back button pointing at wherever the user came from
            // rather than at the screen they just left.
            event.preventDefault();
            window.history.replaceState({}, '', href);
            window.location.reload();
          }}
        >
          {label}
        </a>
      </p>
    </div>
  );
}
